import React from 'react';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy-container">
      <div className="privacy-policy-header">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: March 11, 2025</p>
      </div>

      <div className="privacy-policy-content">
        <section className="policy-section">
          <h2>Introduction</h2>
          <p>
            At Wisdom Dental Clinic, we are committed to protecting your privacy and ensuring the security of your personal information. 
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, 
            use our services, or communicate with us.
          </p>
          <p>
            Please read this Privacy Policy carefully. By accessing or using our services, you acknowledge that you have read, understood, 
            and agree to be bound by the terms described in this policy.
          </p>
        </section>

        <section className="policy-section">
          <h2>Information We Collect</h2>
          
          <h3>Personal Information</h3>
          <p>We may collect personal information that you provide directly to us, including but not limited to:</p>
          <ul>
            <li>Name, address, telephone number, email address, and other contact details</li>
            <li>Date of birth and government-issued identification information</li>
            <li>Medical and dental history, including current medications and allergies</li>
            <li>Dental insurance information and payment details</li>
            <li>Appointment scheduling information</li>
            <li>Communications you send to us</li>
          </ul>

          <h3>Information Collected Automatically</h3>
          <p>When you access our website, we may automatically collect certain information about your device and usage, including:</p>
          <ul>
            <li>IP address and browser type</li>
            <li>Device information (such as your operating system, mobile device ID)</li>
            <li>Pages you visit on our website and time spent on those pages</li>
            <li>Referring website or source through which you accessed our site</li>
            <li>Cookies and similar tracking technologies (as described below)</li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>How We Use Your Information</h2>
          <p>We may use the information we collect for various purposes, including to:</p>
          <ul>
            <li>Provide, maintain, and improve our dental services</li>
            <li>Process and complete transactions, and send related information including appointment confirmations and reminders</li>
            <li>Create and maintain your patient records</li>
            <li>Communicate with you about appointments, treatments, payments, and other service-related matters</li>
            <li>Respond to your comments, questions, and requests</li>
            <li>Send you technical notices, updates, security alerts, and administrative messages</li>
            <li>Monitor and analyze trends, usage, and activities in connection with our services</li>
            <li>Detect, investigate, and prevent fraudulent transactions and other illegal activities</li>
            <li>Comply with legal and regulatory requirements</li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>Disclosure of Your Information</h2>
          <p>We may share your personal information in the following situations:</p>
          <ul>
            <li><strong>Healthcare Providers:</strong> We may share your information with other healthcare providers who are involved in your care.</li>
            <li><strong>Insurance and Payment:</strong> We may share your information with your dental insurance provider to obtain payment for services provided.</li>
            <li><strong>Service Providers:</strong> We may share your information with third-party vendors, consultants, and other service providers who need access to such information to carry out work on our behalf.</li>
            <li><strong>Legal Requirements:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities.</li>
            <li><strong>Business Transfers:</strong> If we are involved in a merger, acquisition, or sale of all or a portion of our assets, your information may be transferred as part of that transaction.</li>
            <li><strong>With Your Consent:</strong> We may share your information with third parties when we have your consent to do so.</li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>Your Rights and Choices</h2>
          <p>Depending on your location, you may have certain rights regarding your personal information, including:</p>
          <ul>
            <li>The right to access and receive a copy of your personal information</li>
            <li>The right to correct or update your personal information</li>
            <li>The right to request deletion of your personal information</li>
            <li>The right to restrict or object to our processing of your personal information</li>
            <li>The right to data portability</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us using the information provided in the "Contact Us" section below.
          </p>
        </section>

        <section className="policy-section">
          <h2>Data Security</h2>
          <p>
            We implement appropriate physical, technical, and administrative safeguards designed to protect the security and confidentiality 
            of your personal information. However, no security system is impenetrable, and we cannot guarantee the security of our systems 100%. 
            In the event that any information under our control is compromised as a result of a breach of security, we will take reasonable steps 
            to investigate the situation and, where appropriate, notify those individuals whose information may have been compromised.
          </p>
        </section>

        <section className="policy-section">
          <h2>Cookies and Similar Technologies</h2>
          <p>
            We and our third-party service providers may use cookies, web beacons, and other tracking technologies to collect information about 
            your browsing activities on our website. Cookies are small data files stored on your hard drive or in device memory that help us improve 
            our website and your experience, see which areas and features of our website are popular, and count visits.
          </p>
          <p>
            Most web browsers are set to accept cookies by default. If you prefer, you can usually choose to set your browser to remove or reject 
            browser cookies. Please note that if you choose to remove or reject cookies, this could affect the availability and functionality of our website.
          </p>
        </section>

        <section className="policy-section">
          <h2>Children's Privacy</h2>
          <p>
            Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children under 18. 
            If we become aware that a child under 18 has provided us with personal information, we will take steps to delete such information. 
            If you become aware that a child has provided us with personal information, please contact us immediately.
          </p>
        </section>

        <section className="policy-section">
          <h2>Third-Party Links</h2>
          <p>
            Our website may contain links to third-party websites and applications. We are not responsible for the privacy practices or the content 
            of these third-party sites. We encourage you to read the privacy policy of every website you visit.
          </p>
        </section>

        <section className="policy-section">
          <h2>Changes to this Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. If we make material changes to how we treat your personal information, 
            we will notify you by email (if we have your email address) or by a notice on our website. The date the Privacy Policy was 
            last revised is indicated at the top of the page. You are responsible for ensuring we have an up-to-date active and deliverable 
            email address for you, and for periodically visiting our website and this Privacy Policy to check for any changes.
          </p>
        </section>

        <section className="policy-section">
          <h2>Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
          </p>
          <div className="contact-info">
            <p><strong>Wisdom Dental Clinic</strong></p>
            <p>120, Huda Market Sector 23, Gurugram, Haryana</p>
            <p>Email: contactwisdomdentalclinic@gmail.com</p>
            <p>Phone: +91-8076353837</p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;